import { Button, Dialog, EmptyState, Spinner, TextAreaField, TextField } from "@hexling/ui";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { PageTitle } from "../../components/Layout";
import { useContent, useListItems, useLists, useServices } from "../../data/AppData";
import { categoryInfo } from "../library/categories";

export function ListPage({ id }: { id: string }) {
  const lists = useLists();
  const items = useListItems(id);
  const index = useContent().data;
  const { user } = useServices();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const list = lists.data?.find((l) => l.id === id);
  const [form, setForm] = useState({ name: "", description: "", icon: "" });

  if (lists.isPending || items.isPending) return <Spinner />;
  if (!list)
    return (
      <EmptyState
        title="List not found"
        action={<Link to="/grimoire">Back to your grimoire</Link>}
      />
    );

  const startEdit = () => {
    setForm({ name: list.name, description: list.description, icon: list.icon });
    setEditing(true);
  };

  return (
    <>
      <PageTitle
        title={`${list.icon ? `${list.icon} ` : ""}${list.name}`}
        subtitle={list.description || undefined}
        back={{ to: "/grimoire", label: "Grimoire" }}
      />
      <div className="toolbar">
        <Button variant="secondary" size="small" onClick={startEdit}>
          Rename list
        </Button>
        <Button variant="danger" size="small" onClick={() => setConfirm(true)}>
          Delete list
        </Button>
      </div>
      {items.data!.length ? (
        <ul className="label-list">
          {items.data!.map((i) => {
            const item = index?.get(i.ref.category, i.ref.slug);
            return (
              <li key={i.id} className="label-row label-row--split">
                <Link
                  to="/library/$category/$slug"
                  params={{ category: i.ref.category, slug: i.ref.slug }}
                >
                  <span aria-hidden="true">{categoryInfo(i.ref.category)?.glyph} </span>
                  {item?.name ?? i.ref.slug}
                </Link>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => user.removeListItem(i.id)}
                  aria-label={`Remove ${item?.name ?? i.ref.slug}`}
                >
                  Remove
                </Button>
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState
          glyph="📜"
          title="This list is empty"
          action={<Link to="/library">Browse the library</Link>}
        >
          Use "Add to list" on any library entry.
        </EmptyState>
      )}

      <Dialog
        open={editing}
        title="Edit list"
        onClose={() => setEditing(false)}
        actions={
          <Button
            onClick={async () => {
              if (!form.name.trim()) return;
              await user.saveList({ ...form, name: form.name.trim() }, id);
              setEditing(false);
            }}
          >
            Save list
          </Button>
        }
      >
        <TextField
          label="Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          maxLength={100}
          error={form.name.trim() ? null : "A list needs a name."}
        />
        <TextField
          label="Icon"
          hint="One emoji, optional"
          value={form.icon}
          onChange={(e) => setForm({ ...form, icon: e.target.value.slice(0, 16) })}
        />
        <TextAreaField
          label="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          maxLength={1000}
        />
      </Dialog>
      <Dialog
        open={confirm}
        title={`Delete "${list.name}"?`}
        onClose={() => setConfirm(false)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirm(false)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                await user.deleteList(id);
                await navigate({ to: "/grimoire" });
              }}
            >
              Delete list
            </Button>
          </>
        }
      >
        <p>The library entries stay in the library; only this list goes.</p>
      </Dialog>
    </>
  );
}
